
import React, { useState, useEffect } from 'react';
import { useData } from '../hooks/useData.js';
import { apiFetch } from '../api/index.js';
import { LoadingSpinner, Button } from '../components/Common.jsx';
import { DynamicIcon } from '../components/icons.jsx';
import { useNotification } from '../context/NotificationContext.jsx';

const ConfigCard = ({ title, icon, children }) => (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-lg text-purple-600">
                    <DynamicIcon name={icon} className="w-5 h-5" />
                </div>
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
            </div>
        </div>
        <div className="p-6 space-y-4">
            {children}
        </div>
    </div>
);

const FeatureToggle = ({ label, checked, onChange, description }) => (
    <div className="flex items-center justify-between py-3 border-b dark:border-gray-700 last:border-0 hover:bg-gray-50 dark:hover:bg-gray-700/50 px-2 rounded -mx-2 transition-colors">
        <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{label}</p>
            {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
        <button
            onClick={onChange}
            type="button"
            className={`${checked ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-600'}
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2`}
            role="switch"
            aria-checked={checked}
        >
            <span
                aria-hidden="true"
                className={`${checked ? 'translate-x-5' : 'translate-x-0'}
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
            />
        </button>
    </div>
);

// Simple Labelled Input Component
const SettingInput = ({ label, type = "text", value, onChange, min, description }) => (
    <div className="py-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            min={min}
            className="block w-full rounded-md border-gray-300 dark:bg-gray-700 dark:border-gray-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm px-3 py-2"
        />
        {description && <p className="mt-1 text-xs text-gray-500">{description}</p>}
    </div>
);


const PlatformGeneralSettings = ({ currentUser, token }) => {
    const platformTenantId = 'platform';
    const { addNotification } = useNotification();

    // Fetch Data
    const { data: features, isLoading: featuresLoading, refresh: refreshFeatures } = useData(`/tenants/${platformTenantId}/features`, token);
    const { data: authConfig, isLoading: authLoading, refresh: refreshAuth } = useData(`/tenants/${platformTenantId}/auth-config`, token);

    // State for Auth Form (Sync with data when loaded)
    const [authForm, setAuthForm] = useState(null);

    useEffect(() => {
        if (authConfig) {
            setAuthForm(authConfig);
        }
    }, [authConfig]);

    const toggleSingleSession = async () => {
        const currentVal = features?.single_session === true;
        try {
            await apiFetch(`/tenants/${platformTenantId}/features`, token, {
                method: 'PUT',
                body: JSON.stringify({ single_session: !currentVal })
            });
            addNotification("Platform security settings updated.");
            refreshFeatures();
        } catch (err) {
            addNotification(err.message, 'error');
        }
    };

    const handleAuthChange = (field, value) => {
        setAuthForm(prev => ({ ...prev, [field]: value }));
    };

    const saveAuthConfig = async () => {
        if (authForm?.session_expiry_minutes < 1) {
            addNotification("Session expiry must be at least 1 minute.", "error");
            return;
        }
        try {
            await apiFetch(`/tenants/${platformTenantId}/auth-config`, token, {
                method: 'PUT',
                body: JSON.stringify(authForm)
            });
            addNotification("Authentication policies updated.");
            refreshAuth();
        } catch (err) {
            addNotification(err.message, 'error');
        }
    };

    if (featuresLoading || authLoading) return <LoadingSpinner />;


    return (
        <div className="max-w-4xl mx-auto py-8 px-4 pb-20">
            {/* Removed Title Block since it's now in a Tab */}
            {/* <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-6">Platform Configuration</h1>
            <p className="text-gray-500 mb-8">Manage global settings and default policies for the entire platform.</p> */}

            <div className="space-y-6">
                {/* 1. Global Session Control */}
                <ConfigCard title="Identity & Session Control" icon="ShieldCheck">
                    <FeatureToggle
                        label="Global Single Session Enforcement"
                        description="Enforce single active session for all users, including Platform Admins. Tenant policies may override this if stricter."
                        checked={features?.single_session === true}
                        onChange={toggleSingleSession}
                    />
                    <FeatureToggle
                        label="Enforce Multi-Factor Authentication (MFA)"
                        description="Require 2FA for all Platform Admins/Users using system credentials."
                        checked={authForm?.mfa_enabled === true}
                        onChange={() => {
                            handleAuthChange('mfa_enabled', !authForm?.mfa_enabled);
                        }}
                    />
                    <div className="pt-3 border-t dark:border-gray-700">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <SettingInput
                                label="Session Expiry (minutes)"
                                type="number"
                                min="1"
                                value={authForm?.session_expiry_minutes ?? 15}
                                onChange={(e) => handleAuthChange('session_expiry_minutes', parseInt(e.target.value))}
                                description="How long before users are automatically logged out. Default: 15 min."
                            />
                        </div>
                        <div className="pt-4 flex justify-end">
                            <Button variant="primary" onClick={saveAuthConfig}>Save Session Settings</Button>
                        </div>
                    </div>
                </ConfigCard>

                {/* 2. Password Policy */}
                <ConfigCard title="Password Policy (Platform & Default)" icon="Lock">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <SettingInput
                            label="Minimum Password Length"
                            type="number"
                            min="6"
                            value={authForm?.password_min_length || 8}
                            onChange={(e) => handleAuthChange('password_min_length', parseInt(e.target.value))}
                            description="Recommended: 12+"
                        />
                        <SettingInput
                            label="Password Expiry (Days)"
                            type="number"
                            min="0"
                            value={authForm?.password_expiry_days || 90}
                            onChange={(e) => handleAuthChange('password_expiry_days', parseInt(e.target.value))}
                            description="Set to 0 to disable expiry."
                        />
                    </div>

                    <h4 className="font-semibold text-sm mt-4 mb-2 text-gray-900 dark:text-gray-100">Complexity Requirements</h4>
                    <div className="space-y-0">
                        <FeatureToggle label="Require Uppercase Letter" checked={authForm?.password_requires_uppercase} onChange={() => handleAuthChange('password_requires_uppercase', !authForm?.password_requires_uppercase)} />
                        <FeatureToggle label="Require Lowercase Letter" checked={authForm?.password_requires_lowercase} onChange={() => handleAuthChange('password_requires_lowercase', !authForm?.password_requires_lowercase)} />
                        <FeatureToggle label="Require Number" checked={authForm?.password_requires_number} onChange={() => handleAuthChange('password_requires_number', !authForm?.password_requires_number)} />
                        <FeatureToggle label="Require Special Character" checked={authForm?.password_requires_special} onChange={() => handleAuthChange('password_requires_special', !authForm?.password_requires_special)} />
                    </div>

                    <div className="pt-4 mt-4 border-t dark:border-gray-700 flex justify-end">
                        <Button variant="primary" onClick={saveAuthConfig}>Save Policy Changes</Button>
                    </div>
                </ConfigCard>
            </div>
        </div>
    );
};

export default PlatformGeneralSettings;
